import React, { FC, useRef, useEffect, useState, useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { ExtendedLead } from '../../../services/types/lead';
import { LeadCardSkeletonGroup } from './LeadCardSkeleton';

const ITEM_HEIGHT = 140;
const BUFFER_SIZE = 5;

const KanbanColumn = styled(Box)(({ theme }) => ({
  flex: '1 1 300px',
  minWidth: '280px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const ColumnHeader = styled(Box)<{ headerColor: string }>(({ theme, headerColor }) => ({
  padding: theme.spacing(2),
  backgroundColor: headerColor,
  color: theme.palette.getContrastText(headerColor),
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
}));

const VirtualizedContainer = styled(Box)({
  flex: 1,
  overflow: 'auto',
  position: 'relative',
});

const VirtualizedContent = styled(Box)({
  position: 'relative',
});

interface VirtualizedKanbanColumnProps {
  config: {
    label: string;
    icon: React.ReactNode;
    color: string;
  };
  leads: ExtendedLead[];
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  isRefreshing: boolean;
  skeletonCount: number;
  onLeadClick: (clientId: number) => void;
  onLoadMore: () => void;
  renderLeadCard: (lead: ExtendedLead, onLeadClick: (clientId: number) => void) => React.ReactNode;
  statusCountLoading: boolean;
}

/**
 * Virtualized Kanban column that renders only visible lead cards for optimal performance
 * Supports infinite scrolling and skeleton loading states
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Lead status for this column
 * @param {Object} props.config - Column configuration (label, icon, color)
 * @param {ExtendedLead[]} props.leads - Array of leads to display
 * @param {number} props.totalCount - Total number of leads for this status
 * @param {boolean} props.hasMore - Whether more leads can be loaded
 * @param {boolean} props.isLoadingMore - Whether currently loading more leads
 * @param {boolean} props.isRefreshing - Whether currently refreshing all leads
 * @param {number} props.skeletonCount - Number of skeleton cards to show
 * @param {Function} props.onLeadClick - Handler for lead card clicks
 * @param {Function} props.onLoadMore - Handler for loading more leads
 * @param {Function} props.renderLeadCard - Function to render individual lead cards
 * @returns {JSX.Element} Virtualized kanban column component
 */
const VirtualizedKanbanColumn: FC<VirtualizedKanbanColumnProps> = ({
  config,
  leads,
  totalCount,
  hasMore,
  isLoadingMore,
  isRefreshing,
  skeletonCount,
  onLeadClick,
  onLoadMore,
  renderLeadCard,
  statusCountLoading,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const { startIndex, endIndex, totalHeight } = useMemo(() => {
    if (!containerHeight) {
      return { startIndex: 0, endIndex: 20, totalHeight: leads.length * ITEM_HEIGHT };
    }

    const visibleStart = Math.floor(scrollTop / ITEM_HEIGHT);
    const visibleEnd = Math.min(leads.length - 1, Math.ceil((scrollTop + containerHeight) / ITEM_HEIGHT));

    return {
      startIndex: Math.max(0, visibleStart - BUFFER_SIZE),
      endIndex: Math.min(leads.length - 1, visibleEnd + BUFFER_SIZE),
      totalHeight: leads.length * ITEM_HEIGHT,
    };
  }, [scrollTop, containerHeight, leads.length]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const newScrollTop = event.currentTarget.scrollTop;
      setScrollTop(newScrollTop);

      const { scrollHeight, clientHeight } = event.currentTarget;
      const scrollBottom = scrollHeight - newScrollTop - clientHeight;

      // Auto-load more when near bottom
      if (scrollBottom < 200 && hasMore && !isLoadingMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const visibleItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    for (let i = startIndex; i <= endIndex; i += 1) {
      const lead = leads[i];
      if (lead) {
        items.push(
          <Box
            key={lead.id}
            sx={{
              position: 'absolute',
              top: i * ITEM_HEIGHT,
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
              padding: 1,
            }}
          >
            {renderLeadCard(lead, onLeadClick)}
          </Box>
        );
      }
    }

    return items;
  }, [startIndex, endIndex, leads, renderLeadCard, onLeadClick]);

  const loadingSkeletons = useMemo(() => {
    if (!isLoadingMore || skeletonCount === 0) return null;

    return (
      <Box sx={{ p: 1 }}>
        <LeadCardSkeletonGroup count={skeletonCount} delay={50} />
      </Box>
    );
  }, [isLoadingMore, skeletonCount]);

  if (leads.length === 0 && !isRefreshing) {
    return (
      <KanbanColumn>
        <ColumnHeader headerColor={config.color}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" sx={{ '& svg': { fontSize: '1.25rem' } }}>
              {config.icon}
            </Box>
            <Typography variant="h6">{config.label}</Typography>
          </Box>
          <Chip
            label={statusCountLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : totalCount}
            size="small"
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'inherit',
              fontWeight: 'bold',
            }}
          />
        </ColumnHeader>

        <Box display="flex" justifyContent="center" alignItems="center" py={4} color="text.secondary" flex={1}>
          <Typography variant="body2">No hay leads en este estado</Typography>
        </Box>
      </KanbanColumn>
    );
  }

  if (isRefreshing && leads.length === 0) {
    return (
      <KanbanColumn>
        <ColumnHeader headerColor={config.color}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" alignItems="center" sx={{ '& svg': { fontSize: '1.25rem' } }}>
              {config.icon}
            </Box>
            <Typography variant="h6">{config.label}</Typography>
          </Box>
          <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.8)' }} />
        </ColumnHeader>

        <Box sx={{ p: 1, flex: 1 }}>
          <LeadCardSkeletonGroup count={8} delay={100} />
        </Box>
      </KanbanColumn>
    );
  }

  return (
    <KanbanColumn>
      <ColumnHeader headerColor={config.color}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" sx={{ '& svg': { fontSize: '1.25rem' } }}>
            {config.icon}
          </Box>
          <Typography variant="h6">{config.label}</Typography>
        </Box>
        <Chip
          label={statusCountLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : totalCount}
          size="small"
          sx={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'inherit',
            fontWeight: 'bold',
          }}
        />
      </ColumnHeader>

      <VirtualizedContainer ref={containerRef} onScroll={handleScroll}>
        <VirtualizedContent style={{ height: totalHeight }}>{visibleItems}</VirtualizedContent>

        {loadingSkeletons}
      </VirtualizedContainer>
    </KanbanColumn>
  );
};

export default VirtualizedKanbanColumn;
